import React, { useState, useCallback, useEffect, useMemo, useRef, forwardRef, useImperativeHandle } from "react";
import PropTypes from "prop-types";
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Typography,
    Skeleton,
    Paper,
    Grid,
    Tooltip,
    CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import ClearIcon from "@mui/icons-material/Clear";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { fetchData, HTTP_STATUS, intl } from "dmsbasereact";
import { useDmsAnalyticsContext } from "./DmsAnalyticsContext";
import { formatCnpj } from "../../utils/formatters";

// Shared 3D drill button styles - floating, translucent when idle
const drillButtonGroupSx = {
    position: "absolute",
    top: -16,
    right: 0,
    zIndex: 10,
    display: "flex",
    gap: 0.25,
    background: "linear-gradient(180deg, #ffffff 0%, #e8e8e8 100%)",
    borderRadius: "16px",
    border: "1px solid #c0c0c0",
    boxShadow: "0 2px 4px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.08), inset 0 1px 0 rgba(255,255,255,0.8)",
    p: "2px 4px",
    opacity: 0.35,
    transition: "opacity 0.25s ease, box-shadow 0.25s ease",
    "&:hover": {
        opacity: 1,
        boxShadow: "0 3px 8px rgba(0,0,0,0.18), 0 1px 3px rgba(0,0,0,0.1), inset 0 1px 0 rgba(255,255,255,0.8)",
    },
};

const drillButtonSx = {
    p: "3px",
    borderRadius: "50%",
    color: "#555",
    transition: "all 0.15s ease",
    "&:hover:not(:disabled)": {
        background: "linear-gradient(180deg, #f0f0f0 0%, #d8d8d8 100%)",
        boxShadow: "inset 0 1px 2px rgba(0,0,0,0.1)",
        color: "#1a365d",
    },
    "&:active:not(:disabled)": {
        background: "linear-gradient(180deg, #d8d8d8 0%, #e0e0e0 100%)",
        boxShadow: "inset 0 2px 3px rgba(0,0,0,0.15)",
    },
    "&:disabled": {
        color: "#ccc",
    },
};

// Shared table container style
const tableContainerSx = {
    borderRadius: "8px",
    border: "1px solid #e2e8f0",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08), 0 4px 12px rgba(0,0,0,0.04)",
    overflow: "auto",
    "&:hover": {
        boxShadow: "0 2px 6px rgba(0,0,0,0.1), 0 6px 16px rgba(0,0,0,0.06)",
    },
    transition: "box-shadow 0.2s ease",
};

// Drill level constants for tree expand/collapse state
const DRILL_LEVEL = {
    COLLAPSED: 0,   // All collapsed
    ROOT: 1,         // Root (Eurofarma) expanded, shows N1 institutions
    N1: 2,           // N1 institutions expanded, shows N2 children
    N2: 3,           // N2 institutions expanded, shows patients
};

// Column configuration matching BI: N1/N2/N3 levels
// N1 = First level (Distributor/Intermediary - direct from Eurofarma)
// N2 = Second level (clients of N1)
// N3 = Third level (Dispensadores - final dispensation to patients)

// Map node to v2 edge-based format
// Each node carries: level, eurofarma_sales, received, dispensacao, transferred, saldo, devolucao
const mapNodeV2 = (node) => {
    const isPatient = node.type === "patient";
    return {
        ...node,
        eurofarma_sales: node.eurofarma_sales || node.vendaEurofarma || 0,
        received: node.received || 0,
        dispensacao: isPatient ? (node.dispensacaoN3 || node.dispensacao || 0) : (node.dispensacao || 0),
        transferred: node.transferred || 0,
        saldo: node.saldo || 0,
        devolucao: node.devolucao || 0,
        level: node.level || 0,
    };
};

// Format number for display
const formatNumber = (value) => {
    if (value === null || value === undefined || value === 0) return "";
    if (value < 0) return <span style={{ color: "#d32f2f", fontWeight: 600 }}>{value.toLocaleString("pt-BR")}</span>;
    return value.toLocaleString("pt-BR");
};

// Format saldo with warning tooltip when negative (dispensation without stock)
const formatSaldo = (value) => {
    if (value === null || value === undefined || value === 0) return "";
    if (value < 0) {
        return (
            <Tooltip title={intl.get("Analytics.Traceability.NegativeBalanceWarning").d("Dispensation without available stock")} arrow>
                <span style={{ color: "#d32f2f", fontWeight: 700, cursor: "help" }}>
                    {value.toLocaleString("pt-BR")} ⚠
                </span>
            </Tooltip>
        );
    }
    return value.toLocaleString("pt-BR");
};

// Mask patient code for privacy
const maskPatientCode = (code) => {
    if (!code) return "--";
    return code;
};

// Header cell with dark blue background
const HeaderCell = ({ children, align = "center", width, minWidth, ...props }) => (
    <TableCell
        align={align}
        sx={{
            backgroundColor: "#1a365d",
            color: "white",
            fontWeight: 600,
            fontSize: "0.7rem",
            py: 0.75,
            px: 0.5,
            borderRight: "1px solid #2d4a6f",
            width,
            minWidth,
            lineHeight: 1.2,
            verticalAlign: "middle",
        }}
        {...props}
    >
        {children}
    </TableCell>
);

// Header cell with info tooltip
const HeaderCellWithInfo = ({ children, tooltip, align = "center", width, minWidth, ...props }) => (
    <HeaderCell align={align} width={width} minWidth={minWidth} {...props}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: align === "left" ? "flex-start" : "center", gap: 0.25 }}>
            {children}
            {tooltip && (
                <Tooltip title={tooltip} arrow placement="top">
                    <InfoOutlinedIcon sx={{ fontSize: 12, opacity: 0.7, cursor: "help" }} />
                </Tooltip>
            )}
        </Box>
    </HeaderCell>
);

// Data cell
const DataCell = ({ children, align = "right", highlight, level = 0, ...props }) => (
    <TableCell
        align={align}
        sx={{
            py: 0.5,
            px: 1,
            fontSize: "0.8rem",
            borderRight: "1px solid #e0e0e0",
            backgroundColor: highlight ? "#e3f2fd" : "inherit",
            pl: level > 0 ? level * 2 : 1,
        }}
        {...props}
    >
        {children}
    </TableCell>
);

// Get display name based on node type
const getNodeDisplayName = (node, level) => {
    if (level === -1) return node.name;  // Root (Eurofarma)
    if (node.number) return node.number;  // Batch
    return node.name;
};

// Get row background color based on level
const getRowBackground = (level) => {
    switch (level) {
        case 0:
            return "#f0f4f8"; // Institution - light blue gray
        case 1:
            return "#f8f9fa"; // Product - light gray
        default:
            return "inherit"; // Batch - white
    }
};

// Get category label for display
const getCategoryLabel = (category) => {
    switch (category) {
        case "eurofarma":
            return intl.get("Analytics.Traceability.CategoryEurofarma").d("Fabricante");
        case "distributor":
            return intl.get("Analytics.Traceability.CategoryDistributor").d("Distribuidor");
        case "intermediary":
            return intl.get("Analytics.Traceability.CategoryIntermediary").d("Intermediária");
        case "dispenser":
            return intl.get("Analytics.Traceability.CategoryDispenser").d("Dispensador");
        case "prescriber":
            return intl.get("Analytics.Traceability.CategoryPrescriber").d("Prescritor");
        case "buyer":
            return intl.get("Analytics.Traceability.CategoryBuyer").d("Comprador");
        default:
            return "";
    }
};

// Get row background color based on level and category
const getRowBackgroundByCategory = (level, category) => {
    if (category === "eurofarma") {
        return "#e8f4fd"; // Eurofarma - light blue
    }
    return getRowBackground(level);
};

// Tree Row Component - handles levels: Eurofarma > Institution only
// Products/batches are NOT shown as children here (they appear in the Product Summary table)
const TreeRow = ({ node: rawNode, level = 0, expandedNodes, onToggle, onSelect, selectedNode, isRoot = false, maxDepth = 1, extraPatients, onLoadMorePatients, loadingMoreNodes }) => {
    const node = mapNodeV2(rawNode);

    // Combine institution children and patients for tree display
    // v2: children = institution nodes, patients = patient nodes (separate fields)
    // v1 compat: children may contain both institutions and patients mixed
    const institutionChildren = (node.children || []).filter(child =>
        !child.id?.startsWith("product-") && !child.id?.startsWith("batch-")
    );
    const basePatients = node.patients || [];
    const extra = (extraPatients && extraPatients[node.id]) || [];
    const patientChildren = [...basePatients, ...extra];
    const allChildren = [...patientChildren, ...institutionChildren];

    const isExpanded = expandedNodes.has(node.id);
    const hasChildren = allChildren.length > 0;
    const indent = level * 24;
    const category = node.category;
    const isSelected = selectedNode === node.id;
    const isPatient = node.type === "patient";

    const handleRowClick = (e) => {
        e.stopPropagation();
        if (onSelect) {
            onSelect(node);
        }
    };

    return (
        <>
            <TableRow
                hover
                onClick={handleRowClick}
                sx={{
                    cursor: "pointer",
                    "&:hover": { backgroundColor: "#e3f2fd" },
                    backgroundColor: isSelected ? "#bbdefb" : getRowBackgroundByCategory(level, category),
                }}
            >
                {/* Name Column */}
                <DataCell align="left" sx={{ pl: `${indent + 8}px !important`, minWidth: 280 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        {hasChildren ? (
                            <Tooltip title={isExpanded ? intl.get("Tooltip.Action.Collapse").d("Collapse") : intl.get("Tooltip.Action.Expand").d("Expand")}>
                                <IconButton
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onToggle(node.id);
                                    }}
                                    sx={{ p: 0.25 }}
                                >
                                    {isExpanded ? (
                                        <RemoveIcon sx={{ fontSize: 16, color: "#1a365d" }} />
                                    ) : (
                                        <AddIcon sx={{ fontSize: 16, color: "#1a365d" }} />
                                    )}
                                </IconButton>
                            </Tooltip>
                        ) : (
                            <Box sx={{ width: 24 }} />
                        )}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: isRoot ? 700 : level === 0 ? 600 : isPatient ? 400 : 500,
                                    fontSize: isPatient ? "0.75rem" : "0.8rem",
                                    color: isRoot ? "#1a365d" : isPatient ? "text.secondary" : "inherit",
                                }}
                            >
                                {isPatient ? node.code : getNodeDisplayName(node, isRoot ? -1 : level)}
                            </Typography>
                            {category && !isPatient && (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontSize: "0.6rem",
                                        backgroundColor: isRoot ? "#1a365d" : "#6b7280",
                                        color: "white",
                                        px: 0.5,
                                        py: 0.25,
                                        borderRadius: 0.5,
                                    }}
                                >
                                    {getCategoryLabel(category)}
                                </Typography>
                            )}
                            {isPatient && (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        fontSize: "0.6rem",
                                        backgroundColor: "#9c27b0",
                                        color: "white",
                                        px: 0.5,
                                        py: 0.25,
                                        borderRadius: 0.5,
                                    }}
                                >
                                    {intl.get("Analytics.Node.Patient").d("Paciente")}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </DataCell>

                {/* Eurofarma Sales */}
                <DataCell>{formatNumber(node.eurofarma_sales)}</DataCell>

                {/* Dynamic N-level columns: Disp. Nx | Ven./Rep. Nx */}
                {Array.from({ length: maxDepth }, (_, i) => {
                    const n = i + 1;
                    const nodeLevel = node.level || 0;
                    const isPatient = node.type === "patient";
                    // For patients: parent level = rendering level - 1 (patient is 1 level below parent)
                    const parentLevel = isPatient ? level - 1 : nodeLevel - 1;
                    const isNodeLevel = nodeLevel === n;
                    const isParentLevel = parentLevel === n && parentLevel > 0;
                    return (
                        <React.Fragment key={`n${n}`}>
                            <DataCell>
                                {isNodeLevel && !isPatient ? formatNumber(node.dispensacao) : ""}
                                {isPatient && isParentLevel ? formatNumber(node.dispensacao) : ""}
                            </DataCell>
                            <DataCell>
                                {isNodeLevel && !isPatient ? formatNumber(node.transferred) : ""}
                                {isParentLevel && !isPatient && !isRoot ? formatNumber(node.received) : ""}
                            </DataCell>
                        </React.Fragment>
                    );
                })}

                {/* Saldo - sticky right */}
                {/* Saldo - sticky right */}
                <TableCell
                    align="right"
                    sx={{
                        position: "sticky",
                        right: 90,
                        zIndex: 1,
                        backgroundColor: isSelected ? "#bbdefb" : (node.type === "patient" ? "#fff" : getRowBackgroundByCategory(level, category)),
                        borderLeft: "1px solid #90a4ae",
                        borderRight: "1px solid #e0e0e0",
                        minWidth: 90,
                        width: 90,
                        py: 0.5,
                        px: 1,
                        fontSize: "0.8rem",
                    }}
                >
                    {node.type !== "patient" ? formatSaldo(node.saldo) : ""}
                </TableCell>

                {/* Devolução - sticky right */}
                <TableCell
                    align="right"
                    sx={{
                        position: "sticky",
                        right: 0,
                        zIndex: 1,
                        backgroundColor: isSelected ? "#bbdefb" : (node.type === "patient" ? "#fff" : getRowBackgroundByCategory(level, category)),
                        minWidth: 90,
                        width: 90,
                        py: 0.5,
                        px: 1,
                        fontSize: "0.8rem",
                    }}
                >
                    {node.type !== "patient" ? formatNumber(node.devolucao) : ""}
                </TableCell>
            </TableRow>

            {isExpanded && (
                <>
                    {/* Patient children first */}
                    {patientChildren.map((child) => (
                        <TreeRow
                            key={child.id}
                            node={child}
                            level={level + 1}
                            expandedNodes={expandedNodes}
                            onToggle={onToggle}
                            onSelect={onSelect}
                            selectedNode={selectedNode}
                            maxDepth={maxDepth}
                            extraPatients={extraPatients}
                            onLoadMorePatients={onLoadMorePatients}
                            loadingMoreNodes={loadingMoreNodes}
                        />
                    ))}

                    {/* Load more patients row — sits right after the last patient, before any sub-institutions */}
                    {!isPatient && onLoadMorePatients && (() => {
                        const totalPat = node.total_patients || 0;
                        const shown = patientChildren.length;
                        if (totalPat <= shown) return null;
                        const isLoadingMore = loadingMoreNodes?.has(node.id);
                        const remaining = totalPat - shown;
                        return (
                            <TableRow key={`${node.id}-load-more`} sx={{ backgroundColor: "transparent" }}>
                                <TableCell
                                    colSpan={20}
                                    sx={{
                                        pl: `${(level + 1) * 24 + 32}px !important`,
                                        py: 0.75,
                                        borderBottom: institutionChildren.length > 0 ? undefined : 0,
                                    }}
                                >
                                    <Box
                                        onClick={isLoadingMore ? undefined : () => onLoadMorePatients(node.id, node.pk, node.source_pk, shown)}
                                        sx={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 0.75,
                                            cursor: isLoadingMore ? "default" : "pointer",
                                            color: isLoadingMore ? "text.disabled" : "#4a7fa5",
                                            borderRadius: 1,
                                            px: 1,
                                            py: 0.25,
                                            border: "1px dashed",
                                            borderColor: isLoadingMore ? "divider" : "#90caf9",
                                            "&:hover": { backgroundColor: isLoadingMore ? "transparent" : "#e3f2fd" },
                                            transition: "background-color 0.15s",
                                        }}
                                    >
                                        {isLoadingMore ? (
                                            <CircularProgress size={11} sx={{ color: "text.disabled" }} />
                                        ) : (
                                            <KeyboardArrowDownIcon sx={{ fontSize: 14, color: "#4a7fa5" }} />
                                        )}
                                        <Typography variant="caption" sx={{ fontSize: "0.7rem", fontWeight: 500 }}>
                                            {isLoadingMore
                                                ? intl.get("Analytics.Traceability.LoadingMore").d("Carregando...")
                                                : intl.get("Analytics.Traceability.LoadMorePatients").d(`Ver mais ${remaining} paciente${remaining !== 1 ? "s" : ""}`)}
                                        </Typography>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        );
                    })()}

                    {/* Institution children after patients */}
                    {institutionChildren.map((child) => (
                        <TreeRow
                            key={child.id}
                            node={child}
                            level={level + 1}
                            expandedNodes={expandedNodes}
                            onToggle={onToggle}
                            onSelect={onSelect}
                            selectedNode={selectedNode}
                            maxDepth={maxDepth}
                            extraPatients={extraPatients}
                            onLoadMorePatients={onLoadMorePatients}
                            loadingMoreNodes={loadingMoreNodes}
                        />
                    ))}
                </>
            )}
        </>
    );
};

// Main Traceability Tree Table - receives expand/collapse state from parent
const TraceabilityTreeTable = ({ data, loading, onNodeSelect, selectedNode, expandedNodes, onToggleNode, maxDepth = 1, extraPatients, onLoadMorePatients, loadingMoreNodes }) => {
    if (loading) {
        return (
            <Box sx={{ p: 2 }}>
                {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} variant="rectangular" height={40} sx={{ mb: 1 }} />
                ))}
            </Box>
        );
    }

    return (
        <Box>
            <TableContainer component={Paper} elevation={0} sx={{ ...tableContainerSx, maxHeight: 500, overflow: "auto" }}>
                <Table size="small" stickyHeader sx={{ minWidth: 900 }}>
                    <TableHead>
                        <TableRow>
                            <HeaderCell align="left" minWidth={280}>
                                {intl.get("Analytics.Traceability.TreeColumn").d("Instituição / Paciente")}
                            </HeaderCell>
                            <HeaderCell minWidth={75}>
                                {intl.get("Analytics.Traceability.EurofarmaSales").d("Eurofarma Sales")}
                            </HeaderCell>
                            {Array.from({ length: maxDepth }, (_, i) => (
                                <React.Fragment key={`header-n${i + 1}`}>
                                    <HeaderCell minWidth={75}>
                                        {`${intl.get("Analytics.Traceability.Dispensacao").d("Dispensação")} N${i + 1}`}
                                    </HeaderCell>
                                    <HeaderCell minWidth={80}>
                                        {`${intl.get("Analytics.Traceability.VendaRepasse").d("Venda/Repasse")} N${i + 1}`}
                                    </HeaderCell>
                                </React.Fragment>
                            ))}
                            <TableCell
                                align="center"
                                sx={{
                                    position: "sticky", right: 90, zIndex: 3,
                                    backgroundColor: "#1a365d", color: "white",
                                    fontWeight: 600, fontSize: "0.7rem",
                                    py: 0.75, px: 0.5,
                                    minWidth: 90, width: 90,
                                    borderLeft: "1px solid #2d4a6f",
                                    borderRight: "1px solid #2d4a6f",
                                }}
                            >
                                {intl.get("Analytics.Traceability.Saldo").d("Saldo")}
                            </TableCell>
                            <TableCell
                                align="center"
                                sx={{
                                    position: "sticky", right: 0, zIndex: 3,
                                    backgroundColor: "#1a365d", color: "white",
                                    fontWeight: 600, fontSize: "0.7rem",
                                    py: 0.75, px: 0.5,
                                    minWidth: 90, width: 90,
                                }}
                            >
                                {intl.get("Analytics.Traceability.Devolucao").d("Devolução")}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(!data?.nodes || data.nodes.length === 0) && !data?.root ? (
                            <TableRow>
                                <TableCell colSpan={3 + maxDepth * 2} align="center" sx={{ py: 4 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        {intl.get("DmsAnalytics.NoData").d("Sem dados disponíveis")}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ) : (
                            <>
                                {/* Eurofarma Root Node with institutions as children */}
                                {data?.root && (
                                    <TreeRow
                                        key={data.root.id}
                                        node={{ ...data.root, children: data.nodes || [] }}
                                        level={0}
                                        expandedNodes={expandedNodes}
                                        onToggle={onToggleNode}
                                        onSelect={onNodeSelect}
                                        selectedNode={selectedNode}
                                        isRoot={true}
                                        maxDepth={maxDepth}
                                        extraPatients={extraPatients}
                                        onLoadMorePatients={onLoadMorePatients}
                                        loadingMoreNodes={loadingMoreNodes}
                                    />
                                )}
                            </>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

// Product/Batch Summary Table
const ProductSummaryTable = forwardRef(({ data, loading, onProductSelect, selectedProductPk, onBatchSelect, selectedBatchNumber }, ref) => {
    const [expandedProducts, setExpandedProducts] = useState(new Set());

    const products = data?.products || [];

    const drillLevel = expandedProducts.size === 0 ? 0 : (products.length > 0 && expandedProducts.size >= products.length ? 1 : 0);

    const handleDrillDown = useCallback(() => {
        if (products.length > 0) {
            setExpandedProducts(new Set(products.map(p => p.pk)));
        }
    }, [products]);

    const handleDrillUp = useCallback(() => {
        setExpandedProducts(new Set());
    }, []);

    useImperativeHandle(ref, () => ({
        expandAll: handleDrillDown,
        collapseAll: handleDrillUp,
        drillDown: handleDrillDown,
        drillUp: handleDrillUp,
        drillLevel,
    }), [handleDrillDown, handleDrillUp, drillLevel]);

    const handleToggle = useCallback((e, productId) => {
        e.stopPropagation();
        setExpandedProducts((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(productId)) {
                newSet.delete(productId);
            } else {
                newSet.add(productId);
            }
            return newSet;
        });
    }, []);

    const handleRowClick = useCallback((product) => {
        if (onProductSelect) {
            onProductSelect(product);
        }
    }, [onProductSelect]);

    const handleBatchClick = useCallback((e, batch, productPk) => {
        e.stopPropagation();
        if (onBatchSelect) {
            onBatchSelect(batch, productPk);
        }
    }, [onBatchSelect]);

    if (loading) {
        return (
            <Box sx={{ p: 2 }}>
                {Array.from({ length: 3 }).map((_, i) => (
                    <Skeleton key={i} variant="rectangular" height={32} sx={{ mb: 1 }} />
                ))}
            </Box>
        );
    }

    const totals = data?.totals || {};

    return (
        <TableContainer component={Paper} elevation={0} sx={{ ...tableContainerSx, maxHeight: 300, overflow: "auto", WebkitOverflowScrolling: "touch" }}>
            <Table size="small" stickyHeader sx={{ "& thead th": { position: "sticky", top: 0, zIndex: 1 } }}>
                <TableHead>
                    <TableRow>
                        <HeaderCell align="left">{intl.get("Analytics.Traceability.Product").d("Produto")}</HeaderCell>
                        <HeaderCell>{intl.get("Analytics.Traceability.VendaRepasse").d("Venda / Repasse")}</HeaderCell>
                        <HeaderCell>{intl.get("Analytics.Traceability.Dispensacao").d("Dispensação")}</HeaderCell>
                        <HeaderCell>{intl.get("Analytics.Traceability.Saldo").d("Saldo")}</HeaderCell>
                        <HeaderCell>{intl.get("Analytics.Traceability.Devolucao").d("Devolução")}</HeaderCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {products.map((product) => (
                        <React.Fragment key={product.pk}>
                            <TableRow
                                hover
                                sx={{
                                    cursor: "pointer",
                                    backgroundColor: selectedProductPk === product.pk ? "#bbdefb" : "inherit",
                                    "&:hover": { backgroundColor: selectedProductPk === product.pk ? "#90caf9" : undefined },
                                }}
                                onClick={() => handleRowClick(product)}
                            >
                                <DataCell align="left">
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                        <Tooltip title={expandedProducts.has(product.pk) ? intl.get("Tooltip.Action.Collapse").d("Collapse") : intl.get("Tooltip.Action.Expand").d("Expand")}>
                                            <IconButton size="small" sx={{ p: 0.25 }} onClick={(e) => handleToggle(e, product.pk)}>
                                                {expandedProducts.has(product.pk) ? (
                                                    <RemoveIcon sx={{ fontSize: 14 }} />
                                                ) : (
                                                    <AddIcon sx={{ fontSize: 14 }} />
                                                )}
                                            </IconButton>
                                        </Tooltip>
                                        <Typography variant="body2" sx={{ fontSize: "0.75rem", fontWeight: 500 }}>
                                            {product.name}
                                        </Typography>
                                    </Box>
                                </DataCell>
                                <DataCell>{formatNumber(product.vendaRepasse)}</DataCell>
                                <DataCell>{formatNumber(product.dispensacao)}</DataCell>
                                <DataCell>{formatSaldo(product.saldo)}</DataCell>
                                <DataCell>{formatNumber(product.devolucao)}</DataCell>
                            </TableRow>
                            {expandedProducts.has(product.pk) && product.batches?.map((batch) => (
                                <TableRow
                                    key={batch.pk || batch.number}
                                    hover
                                    sx={{
                                        cursor: "pointer",
                                        backgroundColor: selectedBatchNumber === batch.number ? "#bbdefb" : "#fafafa",
                                        "&:hover": { backgroundColor: selectedBatchNumber === batch.number ? "#90caf9" : "#f0f0f0" },
                                    }}
                                    onClick={(e) => handleBatchClick(e, batch, product.pk)}
                                >
                                    <DataCell align="left" sx={{ pl: "32px !important" }}>
                                        <Typography variant="body2" sx={{ fontSize: "0.7rem", color: "text.secondary" }}>
                                            {batch.number}
                                        </Typography>
                                    </DataCell>
                                    <DataCell>{formatNumber(batch.vendaRepasse)}</DataCell>
                                    <DataCell>{formatNumber(batch.dispensacao)}</DataCell>
                                    <DataCell>{formatSaldo(batch.saldo)}</DataCell>
                                    <DataCell>{formatNumber(batch.devolucao)}</DataCell>
                                </TableRow>
                            ))}
                        </React.Fragment>
                    ))}
                    {/* Totals Row */}
                    <TableRow sx={{ backgroundColor: "#1a365d" }}>
                        <TableCell sx={{ color: "white", fontWeight: 600, fontSize: "0.75rem" }}>
                            {intl.get("DmsAnalytics.Stock.Total").d("Total")}
                        </TableCell>
                        <TableCell align="right" sx={{ color: "white", fontWeight: 600, fontSize: "0.75rem" }}>
                            {formatNumber(totals.vendaRepasse)}
                        </TableCell>
                        <TableCell align="right" sx={{ color: "white", fontWeight: 600, fontSize: "0.75rem" }}>
                            {formatNumber(totals.dispensacao)}
                        </TableCell>
                        <TableCell align="right" sx={{ color: "white", fontWeight: 600, fontSize: "0.75rem" }}>
                            {formatSaldo(totals.saldo)}
                        </TableCell>
                        <TableCell align="right" sx={{ color: "white", fontWeight: 600, fontSize: "0.75rem" }}>
                            {formatNumber(totals.devolucao)}
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </TableContainer>
    );
});

// Patient Dispensation Table
const PatientDispensationTable = ({ data, loading, onPatientClick, selectedPatientCode, onScroll, loadingMore }) => {
    if (loading) {
        return (
            <Box sx={{ p: 2 }}>
                {Array.from({ length: 3 }).map((_, i) => (
                    <Skeleton key={i} variant="rectangular" height={32} sx={{ mb: 1 }} />
                ))}
            </Box>
        );
    }

    const patients = data || [];

    return (
        <TableContainer
            component={Paper}
            elevation={0}
            sx={{ ...tableContainerSx, maxHeight: 300 }}
            onScroll={onScroll}
        >
            <Table size="small" stickyHeader>
                <TableHead>
                    <TableRow>
                        <HeaderCell align="left">
                            {intl.get("Analytics.Traceability.PatientCode").d("Nº Paciente Programa Lena")}
                        </HeaderCell>
                        <HeaderCell>
                            {intl.get("Analytics.Traceability.DispensationDate").d("Data Dispensação")}
                        </HeaderCell>
                        <HeaderCell>
                            {intl.get("Analytics.Traceability.Quantity").d("Quantidade")}
                        </HeaderCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {patients.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={3} align="center" sx={{ py: 2 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.75rem" }}>
                                    {intl.get("DmsAnalytics.NoData").d("Sem dados disponíveis")}
                                </Typography>
                            </TableCell>
                        </TableRow>
                    ) : (
                        patients.map((patient, index) => (
                            <TableRow
                                key={`${patient.code}-${index}`}
                                hover
                                sx={{
                                    cursor: onPatientClick ? "pointer" : "default",
                                    backgroundColor: selectedPatientCode === patient.code ? "#bbdefb" : "inherit",
                                    "&:hover": { backgroundColor: selectedPatientCode === patient.code ? "#90caf9" : undefined },
                                }}
                                onClick={() => onPatientClick && onPatientClick(patient)}
                            >
                                <DataCell align="left">
                                    <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                                        {maskPatientCode(patient.code)}
                                    </Typography>
                                </DataCell>
                                <DataCell align="center">
                                    <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                                        {patient.date}
                                    </Typography>
                                </DataCell>
                                <DataCell>
                                    <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
                                        {formatNumber(patient.quantity)}
                                    </Typography>
                                </DataCell>
                            </TableRow>
                        ))
                    )}
                    {loadingMore && (
                        <TableRow>
                            <TableCell colSpan={3} align="center" sx={{ py: 1 }}>
                                <Skeleton variant="rectangular" height={20} />
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

// Tree Expand Toggle - 3-state cycling icon: collapsed → mixed → expanded
// This component is exported for use in DmsAnalytics.Header
export const TreeExpandToggle = ({ expandState, onExpandAll, onCollapseAll }) => {
    // expandState: "collapsed" | "mixed" | "expanded"
    // Cycles: collapsed → expanded → collapsed
    // When mixed, next click goes to expanded

    const handleClick = useCallback(() => {
        if (expandState === "expanded") {
            onCollapseAll();
        } else {
            onExpandAll();
        }
    }, [expandState, onExpandAll, onCollapseAll]);

    const getIconConfig = () => {
        switch (expandState) {
            case "expanded":
                return {
                    icon: <UnfoldLessIcon fontSize="small" />,
                    tooltip: intl.get("Analytics.Tree.CollapseAll").d("Recolher Todos"),
                };
            case "mixed":
                return {
                    icon: <AccountTreeIcon fontSize="small" />,
                    tooltip: intl.get("Analytics.Tree.ExpandAll").d("Expandir Todos"),
                };
            case "collapsed":
            default:
                return {
                    icon: <UnfoldMoreIcon fontSize="small" />,
                    tooltip: intl.get("Analytics.Tree.ExpandAll").d("Expandir Todos"),
                };
        }
    };

    const { icon, tooltip } = getIconConfig();

    return (
        <Tooltip title={tooltip}>
            <IconButton size="small" onClick={handleClick} sx={{ color: "inherit" }}>
                {icon}
            </IconButton>
        </Tooltip>
    );
};

TreeExpandToggle.propTypes = {
    expandState: PropTypes.oneOf(["collapsed", "mixed", "expanded"]),
    onExpandAll: PropTypes.func.isRequired,
    onCollapseAll: PropTypes.func.isRequired,
};

// Main Traceability Component - uses forwardRef to expose expand/collapse methods
export const DmsAnalyticsTraceability = forwardRef(({ url, sx, onExpandStateChange, ...props }, ref) => {
    const { queryParams, refreshKey } = useDmsAnalyticsContext();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selectedNode, setSelectedNode] = useState(null);
    const [selectedNodeData, setSelectedNodeData] = useState(null);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedBatch, setSelectedBatch] = useState(null);
    const [selectedPatientDisp, setSelectedPatientDisp] = useState(null);
    const [expandedNodes, setExpandedNodes] = useState(new Set());
    const productSummaryRef = React.useRef(null);

    // Patient dispensations — loaded separately with infinite scroll
    const [patientDisp, setPatientDisp] = useState([]);
    const [patientDispLoading, setPatientDispLoading] = useState(false);
    const [patientDispHasMore, setPatientDispHasMore] = useState(false);
    const patientDispOffsetRef = useRef(0);
    const patientDispLoadingRef = useRef(false);

    // Extra patients loaded on demand per tree node (nodeId -> TraceabilityPatientNodeSchema[])
    const [extraPatients, setExtraPatients] = useState({});
    const [loadingMoreNodes, setLoadingMoreNodes] = useState(new Set());

    const loadData = useCallback(async () => {
        if (!url) return;

        setLoading(true);
        try {
            const separator = url.includes("?") ? "&" : "?";
            const fullUrl = queryParams ? `${url}${separator}${queryParams}` : url;
            const response = await fetchData(fullUrl, "GET", true);

            if (response.status === HTTP_STATUS.OK_200) {
                setData(response.data?.data || null);
            }
        } catch (err) {
            console.error("Error loading traceability data:", err);
            setData(null);
        } finally {
            setLoading(false);
        }
    }, [url, queryParams]);

    useEffect(() => {
        loadData();
    }, [loadData, refreshKey]);

    // Compute server-side filter params for patient dispensations from current selection
    const computePatientFilters = useCallback(() => {
        const params = new URLSearchParams();
        if (queryParams) {
            new URLSearchParams(queryParams).forEach((v, k) => params.set(k, v));
        }
        if (selectedBatch) {
            if (selectedBatch.pk) params.set("batch_pk", String(selectedBatch.pk));
        } else if (selectedProduct) {
            if (selectedProduct.pk) params.set("product_pk", String(selectedProduct.pk));
        } else if (selectedNodeData && selectedNodeData.type === "patient") {
            if (selectedNodeData.code) params.set("patient_code", selectedNodeData.code);
        } else if (selectedNodeData && selectedNodeData.pk) {
            const collectInstPks = (node) => {
                const pks = [node.pk];
                (node.children || [])
                    .filter(c => c.id?.startsWith("inst-") || c.id?.startsWith("edge-"))
                    .forEach(c => pks.push(...collectInstPks(c)));
                return pks;
            };
            const instPks = collectInstPks(selectedNodeData);
            params.set("institution_pks", instPks.join(","));
        }
        return params.toString();
    }, [queryParams, selectedBatch, selectedProduct, selectedNodeData]);

    const fetchPatientDisp = useCallback(async (offset, filterParams) => {
        if (!url || patientDispLoadingRef.current) return;
        patientDispLoadingRef.current = true;
        if (offset === 0) {
            setPatientDispLoading(true);
        }
        try {
            const base = url.replace("/traceability/v2", "/traceability/v2/patient-dispensations");
            const params = new URLSearchParams(filterParams);
            params.set("offset", String(offset));
            params.set("limit", "1000");
            const response = await fetchData(`${base}?${params.toString()}`, "GET", true);
            if (response.status === HTTP_STATUS.OK_200) {
                const pd = response.data?.data;
                setPatientDisp(prev => offset === 0 ? (pd?.patients || []) : [...prev, ...(pd?.patients || [])]);
                setPatientDispHasMore(pd?.has_more || false);
                patientDispOffsetRef.current = offset + (pd?.patients?.length || 0);
            }
        } catch (err) {
            console.error("Error loading patient dispensations:", err);
        } finally {
            patientDispLoadingRef.current = false;
            if (offset === 0) setPatientDispLoading(false);
        }
    }, [url]);

    // Reset and reload patient dispensations when URL/queryParams/refreshKey change
    useEffect(() => {
        if (!url) return;
        patientDispOffsetRef.current = 0;
        patientDispLoadingRef.current = false;
        const filters = computePatientFilters();
        fetchPatientDisp(0, filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [url, queryParams, refreshKey]);

    // Reload patient dispensations when tree/product/batch selection changes
    useEffect(() => {
        if (!url) return;
        patientDispOffsetRef.current = 0;
        patientDispLoadingRef.current = false;
        const filters = computePatientFilters();
        fetchPatientDisp(0, filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedNodeData, selectedProduct, selectedBatch]);

    const handlePatientTableScroll = useCallback((e) => {
        const el = e.target;
        const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 50;
        if (nearBottom && patientDispHasMore && !patientDispLoadingRef.current) {
            const filters = computePatientFilters();
            fetchPatientDisp(patientDispOffsetRef.current, filters);
        }
    }, [patientDispHasMore, computePatientFilters, fetchPatientDisp]);

    const handleLoadMorePatients = useCallback(async (nodeId, institutionPk, sourcePk, currentOffset) => {
        if (!url || loadingMoreNodes.has(nodeId)) return;
        setLoadingMoreNodes(prev => new Set([...prev, nodeId]));
        try {
            const base = url.replace("/traceability/v2", "/traceability/v2/institution-patients");
            const params = new URLSearchParams(queryParams || "");
            params.set("institution_pk", String(institutionPk));
            params.set("offset", String(currentOffset));
            params.set("limit", "50");
            if (sourcePk != null) {
                params.set("source_pk", String(sourcePk));
            }
            const response = await fetchData(`${base}?${params.toString()}`, "GET", true);
            if (response.status === HTTP_STATUS.OK_200) {
                const newPatients = response.data?.data?.patients || [];
                setExtraPatients(prev => ({
                    ...prev,
                    [nodeId]: [...(prev[nodeId] || []), ...newPatients],
                }));
            }
        } catch (err) {
            console.error("Error loading more patients:", err);
        } finally {
            setLoadingMoreNodes(prev => {
                const next = new Set(prev);
                next.delete(nodeId);
                return next;
            });
        }
    }, [url, queryParams, loadingMoreNodes]);

    // Reset extra patients when main data reloads
    useEffect(() => {
        setExtraPatients({});
    }, [data]);

    // Collect all expandable node IDs (only institution-level, not products/batches)
    const getAllNodeIds = useCallback(() => {
        const seen = new Set();
        const collectIds = (nodes) => {
            nodes?.forEach((node) => {
                if (!node.id || seen.has(node.id)) return;
                const institutionChildren = (node.children || []).filter(child =>
                    !child.id?.startsWith("product-") && !child.id?.startsWith("batch-")
                );
                const patientChildren = node.patients || [];
                if (institutionChildren.length > 0 || patientChildren.length > 0) {
                    seen.add(node.id);
                    collectIds(institutionChildren);
                }
            });
        };
        if (data?.tree?.root) {
            const rootChildren = data.tree.nodes || [];
            if (rootChildren.length > 0) {
                seen.add(data.tree.root.id);
                collectIds(rootChildren);
            }
        }
        return [...seen];
    }, [data]);

    // Tree expand/collapse handlers
    const handleToggleNode = useCallback((nodeId) => {
        setExpandedNodes((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(nodeId)) {
                newSet.delete(nodeId);
            } else {
                newSet.add(nodeId);
            }
            return newSet;
        });
    }, []);

    const handleExpandAll = useCallback(() => {
        const allIds = getAllNodeIds();
        setExpandedNodes(new Set(allIds));
        productSummaryRef.current?.expandAll();
    }, [getAllNodeIds]);

    const handleCollapseAll = useCallback(() => {
        setExpandedNodes(new Set());
        productSummaryRef.current?.collapseAll();
    }, []);

    // Get current drill level using DRILL_LEVEL constants
    const getDrillLevel = useCallback(() => {
        if (expandedNodes.size === 0) return DRILL_LEVEL.COLLAPSED;
        const rootId = data?.tree?.root?.id;
        if (!rootId || !expandedNodes.has(rootId)) return DRILL_LEVEL.COLLAPSED;
        const instNodes = data?.tree?.nodes || [];
        const hasExpandedInst = instNodes.some(n => expandedNodes.has(n.id));
        if (!hasExpandedInst) return DRILL_LEVEL.ROOT;
        // Check if sub-institutions (N2) are expanded
        const hasExpandedSub = instNodes.some(n => {
            const instChildren = (n.children || []).filter(c => c.id?.startsWith("inst-") || c.id?.startsWith("edge-"));
            return instChildren.some(c => expandedNodes.has(c.id));
        });
        return hasExpandedSub ? DRILL_LEVEL.N2 : DRILL_LEVEL.N1;
    }, [expandedNodes, data?.tree]);

    // Drill down: expand one level deeper
    const handleDrillDown = useCallback(() => {
        const level = getDrillLevel();
        if (level === DRILL_LEVEL.COLLAPSED) {
            // Expand root → show N1 institutions
            const rootId = data?.tree?.root?.id;
            if (rootId) setExpandedNodes(new Set([rootId]));
        } else if (level === DRILL_LEVEL.ROOT) {
            // Expand N1 institutions
            const instIds = (data?.tree?.nodes || []).map(n => n.id);
            setExpandedNodes(prev => new Set([...prev, ...instIds]));
        } else if (level === DRILL_LEVEL.N1) {
            // Expand all (N2 + patients)
            const allIds = getAllNodeIds();
            setExpandedNodes(new Set(allIds));
        }
    }, [getDrillLevel, data?.tree, getAllNodeIds]);

    // Drill up: collapse one level
    const handleDrillUp = useCallback(() => {
        const level = getDrillLevel();
        if (level === DRILL_LEVEL.N2) {
            // Collapse sub-institutions, keep root + N1 expanded
            const rootId = data?.tree?.root?.id;
            const instIds = (data?.tree?.nodes || []).map(n => n.id);
            setExpandedNodes(new Set([rootId, ...instIds]));
        } else if (level === DRILL_LEVEL.N1) {
            // Collapse N1, keep root expanded
            const rootId = data?.tree?.root?.id;
            setExpandedNodes(rootId ? new Set([rootId]) : new Set());
        } else if (level === DRILL_LEVEL.ROOT) {
            // Collapse root
            setExpandedNodes(new Set());
        }
    }, [getDrillLevel, data?.tree]);

    // Derive expand state as a value (not a function) for reliable downstream updates
    const expandState = useMemo(() => {
        const allIds = getAllNodeIds();
        if (allIds.length === 0) return "collapsed";
        if (expandedNodes.size === 0) return "collapsed";
        // Use every() so duplicates in allIds don't cause a false "mixed" result
        const allExpanded = allIds.every(id => expandedNodes.has(id));
        return allExpanded ? "expanded" : "mixed";
    }, [getAllNodeIds, expandedNodes]);

    // Expose methods to parent via ref
    useImperativeHandle(ref, () => ({
        expandAll: handleExpandAll,
        collapseAll: handleCollapseAll,
        expandState,
        drillDown: handleDrillDown,
        drillUp: handleDrillUp,
        drillLevel: getDrillLevel(),
    }), [handleExpandAll, handleCollapseAll, expandState, handleDrillDown, handleDrillUp, getDrillLevel]);

    // Calculate the deepest visible N-level based on expanded nodes
    const getVisibleDepth = useCallback(() => {
        if (!data?.tree?.nodes || expandedNodes.size === 0) return 0;
        const rootId = data?.tree?.root?.id;
        if (!rootId || !expandedNodes.has(rootId)) return 0;

        let depth = 1; // At least N1 is visible when root is expanded
        const walkNodes = (nodes) => {
            for (const node of nodes) {
                if (node.level && node.level > depth) depth = node.level;
                if (expandedNodes.has(node.id)) {
                    const children = (node.children || []).filter(c =>
                        c.id?.startsWith("inst-") || c.id?.startsWith("edge-")
                    );
                    if (children.length > 0) walkNodes(children);
                }
            }
        };
        walkNodes(data.tree.nodes || []);
        return depth;
    }, [data?.tree, expandedNodes]);

    // Notify parent when expand state changes
    useEffect(() => {
        if (onExpandStateChange) {
            onExpandStateChange(expandState);
        }
    }, [expandState, onExpandStateChange]);

    // Handle node selection - filters the product summary and patient tables
    const handleNodeSelect = useCallback((node) => {
        if (node === null) {
            setSelectedNode(null);
            setSelectedNodeData(null);
        } else {
            setSelectedNode(node.id);
            setSelectedNodeData(node);
        }
        // Clear product/batch/patient selection when tree selection changes
        setSelectedProduct(null);
        setSelectedBatch(null);
        setSelectedPatientDisp(null);
    }, []);

    // Handle product selection - filters the tree table and patient dispensations
    const handleProductSelect = useCallback((product) => {
        if (product === null || (selectedProduct && selectedProduct.pk === product.pk)) {
            // Deselect if clicking same product or null
            setSelectedProduct(null);
        } else {
            setSelectedProduct(product);
            // Clear tree selection when product selection changes
            setSelectedNode(null);
            setSelectedNodeData(null);
        }
        // Always clear batch/patient when product changes
        setSelectedBatch(null);
        setSelectedPatientDisp(null);
    }, [selectedProduct]);

    // Handle batch selection - filters the tree table and patient dispensations by specific batch
    const handleBatchSelect = useCallback((batch, productPk) => {
        if (batch === null || (selectedBatch && selectedBatch.number === batch.number)) {
            setSelectedBatch(null);
        } else {
            setSelectedBatch({ ...batch, productPk });
            // Clear tree, product, and patient selection
            setSelectedNode(null);
            setSelectedNodeData(null);
            setSelectedProduct(null);
            setSelectedPatientDisp(null);
        }
    }, [selectedBatch]);

    // Helper to find patient node recursively in tree
    const findPatientInTree = useCallback((nodes, code) => {
        for (const node of nodes) {
            // Search in v2 patients array
            const patientFromV2 = (node.patients || []).find(p => p.code === code);
            if (patientFromV2) return { patientNode: patientFromV2, instNode: node };
            // Search in v1 children (backward compat)
            const patientFromChildren = (node.children || []).find(child =>
                child.code === code && child.type === "patient"
            );
            if (patientFromChildren) return { patientNode: patientFromChildren, instNode: node };
            // Search in nested institution children
            const instChildren = (node.children || []).filter(c => c.id?.startsWith("inst-") || c.id?.startsWith("edge-") || c.id?.startsWith("edge-"));
            if (instChildren.length > 0) {
                const found = findPatientInTree(instChildren, code);
                if (found) return found;
            }
        }
        return null;
    }, []);

    // Handle patient dispensation row click - filters main tree and product summary (NOT patient table itself)
    const handlePatientDispClick = useCallback((patient) => {
        if (!data?.tree?.nodes) return;

        // Toggle off if clicking same patient
        if (selectedPatientDisp && selectedPatientDisp.code === patient.code) {
            setSelectedPatientDisp(null);
            return;
        }

        // Find the patient node recursively in tree to get its product data
        const found = findPatientInTree(data.tree.nodes, patient.code);
        let patientNodeData = null;
        if (found) {
            patientNodeData = { ...found.patientNode, institutionPk: found.instNode.pk };
            // Expand the tree so the patient is visible
            setExpandedNodes(prev => {
                const newSet = new Set(prev);
                newSet.add("eurofarma-root");
                newSet.add(found.instNode.id);
                // Also expand parent institutions up the chain
                for (const topNode of data.tree.nodes) {
                    const instChildren = (topNode.children || []).filter(c =>
                        c.id?.startsWith("inst-") || c.id?.startsWith("edge-") || c.id?.startsWith("edge-")
                    );
                    if (instChildren.some(c => c.id === found.instNode.id)) {
                        newSet.add(topNode.id);
                    }
                }
                return newSet;
            });
        }

        setSelectedPatientDisp(patientNodeData || { code: patient.code, institution_pk: patient.institution_pk });
        // Clear other selections
        setSelectedNode(null);
        setSelectedNodeData(null);
        setSelectedProduct(null);
        setSelectedBatch(null);
    }, [data?.tree?.nodes, selectedPatientDisp, findPatientInTree]);

    // Filter product summary based on selected node or patient dispensation click
    // When a patient/institution is selected, show their specific product data
    const getFilteredProductSummary = useCallback(() => {
        if (!data?.productSummary) return data?.productSummary;

        // Use selectedPatientDisp (from patient dispensation table click) or selectedNodeData (from tree click)
        const activePatient = selectedPatientDisp || selectedNodeData;
        if (!activePatient) return data?.productSummary;

        // If a patient is selected, show products dispensed to that patient with their quantities
        if (activePatient.type === "patient" && activePatient.products?.length > 0) {
            // Build product summary from patient's products including batches
            const patientProducts = activePatient.products.map(p => ({
                pk: p.pk,
                name: p.name,
                vendaRepasse: 0,
                dispensacao: p.dispensacao,
                saldo: 0,
                devolucao: 0,
                batches: (p.batches || []).map(b => ({
                    number: b.number,
                    vendaRepasse: 0,
                    dispensacao: b.dispensacao,
                    saldo: 0,
                    devolucao: 0,
                })),
            }));
            const totals = {
                vendaRepasse: 0,
                dispensacao: patientProducts.reduce((sum, p) => sum + p.dispensacao, 0),
                saldo: 0,
                devolucao: 0,
            };
            return { products: patientProducts, totals };
        }

        // If an institution is selected, use its productBreakdown to filter the product summary
        if (activePatient.pk) {
            // Collect productBreakdown from the selected node and its institution children
            const collectBreakdowns = (node) => {
                const breakdowns = [...(node.productBreakdown || [])];
                (node.children || []).forEach(child => {
                    if (child.id?.startsWith("inst-") || child.id?.startsWith("edge-")) {
                        breakdowns.push(...collectBreakdowns(child));
                    }
                });
                return breakdowns;
            };

            const allBreakdowns = collectBreakdowns(activePatient);

            if (allBreakdowns.length > 0) {
                // Aggregate by product pk
                const productMap = new Map();
                allBreakdowns.forEach(pb => {
                    const fullProduct = data?.productSummary?.products?.find(p => p.pk === pb.pk);
                    if (!productMap.has(pb.pk)) {
                        productMap.set(pb.pk, {
                            pk: pb.pk,
                            name: fullProduct?.name || `#${pb.pk}`,
                            vendaRepasse: 0,
                            dispensacao: 0,
                            saldo: 0,
                            devolucao: 0,
                            batchMap: new Map(),
                        });
                    }
                    const prod = productMap.get(pb.pk);
                    prod.vendaRepasse += pb.vendaEurofarma || 0;
                    prod.dispensacao += pb.dispensacao || 0;
                    prod.saldo += pb.saldo || 0;
                    prod.devolucao += pb.devolucao || 0;

                    (pb.batches || []).forEach(b => {
                        if (!prod.batchMap.has(b.pk)) {
                            const fullBatch = fullProduct?.batches?.find(fb => fb.pk === b.pk);
                            prod.batchMap.set(b.pk, {
                                pk: b.pk,
                                number: fullBatch?.number || b.number || `#${b.pk}`,
                                vendaRepasse: 0,
                                dispensacao: 0,
                                saldo: 0,
                                devolucao: 0,
                            });
                        }
                        const batch = prod.batchMap.get(b.pk);
                        batch.vendaRepasse += b.vendaEurofarma || 0;
                        batch.dispensacao += b.dispensacao || 0;
                        batch.saldo += b.saldo || 0;
                        batch.devolucao += b.devolucao || 0;
                    });
                });

                // Include untracked patient dispensations (batches not in productBreakdown)
                const collectPatientDisp = (node) => {
                    (node.patients || []).forEach(patient => {
                        (patient.products || []).forEach(prod => {
                            (prod.batches || []).forEach(b => {
                                const key = `${prod.pk}-${b.pk}`;
                                // Only add if this batch isn't already tracked via productBreakdown
                                if (!productMap.has(prod.pk) || !productMap.get(prod.pk).batchMap.has(b.pk)) {
                                    const fullProduct = data?.productSummary?.products?.find(p => p.pk === prod.pk);
                                    const fullBatch = fullProduct?.batches?.find(fb => fb.pk === b.pk);
                                    if (!productMap.has(prod.pk)) {
                                        productMap.set(prod.pk, {
                                            pk: prod.pk,
                                            name: fullProduct?.name || prod.name || `#${prod.pk}`,
                                            vendaRepasse: 0,
                                            dispensacao: 0,
                                            saldo: 0,
                                            devolucao: 0,
                                            batchMap: new Map(),
                                        });
                                    }
                                    const p = productMap.get(prod.pk);
                                    p.dispensacao += b.dispensacao || 0;
                                    p.saldo -= b.dispensacao || 0;

                                    if (!p.batchMap.has(b.pk)) {
                                        p.batchMap.set(b.pk, {
                                            pk: b.pk,
                                            number: fullBatch?.number || b.number || `#${b.pk}`,
                                            vendaRepasse: 0,
                                            dispensacao: 0,
                                            saldo: 0,
                                            devolucao: 0,
                                        });
                                    }
                                    const batch = p.batchMap.get(b.pk);
                                    batch.dispensacao += b.dispensacao || 0;
                                    batch.saldo -= b.dispensacao || 0;
                                }
                            });
                        });
                    });
                    (node.children || []).forEach(child => {
                        if (child.id?.startsWith("inst-") || child.id?.startsWith("edge-") || child.id?.startsWith("untracked-")) {
                            collectPatientDisp(child);
                        }
                    });
                };
                collectPatientDisp(activePatient);

                const instProducts = Array.from(productMap.values()).map(p => ({
                    ...p,
                    batches: Array.from(p.batchMap.values()),
                }));
                instProducts.forEach(p => delete p.batchMap);

                const totals = {
                    vendaRepasse: instProducts.reduce((sum, p) => sum + p.vendaRepasse, 0),
                    dispensacao: instProducts.reduce((sum, p) => sum + p.dispensacao, 0),
                    saldo: instProducts.reduce((sum, p) => sum + p.saldo, 0),
                    devolucao: instProducts.reduce((sum, p) => sum + p.devolucao, 0),
                };
                return { products: instProducts, totals };
            }
        }

        return data?.productSummary;
    }, [data?.productSummary, selectedNodeData, selectedPatientDisp]);

    // Filter tree data based on selected product, batch, or patient dispensation
    // Uses productBreakdown per institution for accurate per-product/batch values
    const getFilteredTreeData = useCallback(() => {
        if (!data?.tree) return data?.tree;
        if (!selectedProduct && !selectedBatch && !selectedPatientDisp) return data?.tree;

        let filteredNodes;

        if (selectedPatientDisp) {
            // Filter to the institution that has this patient (may be nested)
            const instPk = selectedPatientDisp.institutionPk || selectedPatientDisp.institution_pk;
            filteredNodes = (data.tree.nodes || []).filter(node => {
                if (node.pk === instPk) return true;
                // Check nested institution children
                const hasNestedInst = (n) => (n.children || []).filter(c => c.id?.startsWith("inst-") || c.id?.startsWith("edge-")).some(c => c.pk === instPk || hasNestedInst(c));
                return hasNestedInst(node);
            });
        } else if (selectedBatch) {
            // Filter by specific batch within a product
            const batchPk = selectedBatch.pk;
            const productPk = selectedBatch.productPk;
            filteredNodes = (data.tree.nodes || []).reduce((acc, node) => {
                const prodData = (node.productBreakdown || []).find(p => p.pk === productPk);
                if (!prodData) return acc;
                const batchData = (prodData.batches || []).find(b => b.pk === batchPk);
                if (!batchData) return acc;
                acc.push({
                    ...node,
                    received: batchData.vendaEurofarma,
                    eurofarma_sales: batchData.vendaEurofarma,
                    dispensacao: batchData.dispensacao,
                    saldo: batchData.saldo,
                    devolucao: batchData.devolucao,
                });
                return acc;
            }, []);
        } else {
            // Filter by product
            const productPk = selectedProduct.pk;
            filteredNodes = (data.tree.nodes || []).reduce((acc, node) => {
                const prodData = (node.productBreakdown || []).find(p => p.pk === productPk);
                if (!prodData) return acc;
                acc.push({
                    ...node,
                    received: prodData.vendaEurofarma,
                    eurofarma_sales: prodData.vendaEurofarma,
                    dispensacao: prodData.dispensacao,
                    saldo: prodData.saldo,
                    devolucao: prodData.devolucao,
                });
                return acc;
            }, []);
        }

        // Recalculate Eurofarma root by summing filtered institution values
        const sumField = (field) => filteredNodes.reduce((sum, n) => sum + (n[field] || 0), 0);
        const totalEurofarmaSales = sumField("eurofarma_sales");
        const totalDispensacao = sumField("dispensacao");
        const totalDevolucao = sumField("devolucao");

        const filteredRoot = data.tree.root ? {
            ...data.tree.root,
            eurofarma_sales: totalEurofarmaSales,
            dispensacao: totalDispensacao,
            saldo: Math.max(0, totalEurofarmaSales - totalDispensacao - totalDevolucao),
            devolucao: totalDevolucao,
        } : null;

        return { ...data.tree, root: filteredRoot, nodes: filteredNodes };
    }, [data?.tree, selectedProduct, selectedBatch, selectedPatientDisp]);

    return (
        <Box sx={{ ...sx }} {...props}>
            {/* Selection indicator - shown when any cross-filter is active */}
            {(selectedNodeData || selectedProduct || selectedBatch || selectedPatientDisp) && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1, py: 0.5, mb: 1, backgroundColor: "#e3f2fd", borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500, fontSize: "0.75rem" }}>
                        {intl.get("Analytics.Traceability.FilteredBy").d("Filtrado por")}:
                    </Typography>
                    <Typography variant="body2" color="primary" sx={{ fontSize: "0.75rem" }}>
                        {selectedPatientDisp
                            ? `${intl.get("Analytics.Node.Patient").d("Paciente")} ${selectedPatientDisp.code}`
                            : selectedBatch
                                ? `${intl.get("Analytics.Traceability.Batch").d("Lote")} ${selectedBatch.number}`
                                : selectedProduct
                                    ? selectedProduct.name
                                    : selectedNodeData.type === "patient"
                                        ? selectedNodeData.code
                                        : selectedNodeData.name}
                    </Typography>
                    <Tooltip title={intl.get("Analytics.Tree.ClearSelection").d("Limpar Seleção")}>
                        <IconButton size="small" onClick={() => {
                            handleNodeSelect(null);
                            setSelectedProduct(null);
                            setSelectedBatch(null);
                            setSelectedPatientDisp(null);
                        }} sx={{ p: 0.25 }}>
                            <ClearIcon sx={{ fontSize: 16 }} color="error" />
                        </IconButton>
                    </Tooltip>
                </Box>
            )}

            {/* Main Tree Table */}
            <Box sx={{ mb: 3, position: "relative" }}>
                {/* Floating Drill Up/Down buttons */}
                <Box sx={drillButtonGroupSx}>
                    <Tooltip title={intl.get("Analytics.Tree.DrillUp").d("Recolher Nível")}>
                        <span>
                            <IconButton
                                size="small"
                                onClick={handleDrillUp}
                                disabled={getDrillLevel() === DRILL_LEVEL.COLLAPSED}
                                sx={drillButtonSx}
                            >
                                <KeyboardArrowUpIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title={intl.get("Analytics.Tree.DrillDown").d("Expandir Nível")}>
                        <span>
                            <IconButton
                                size="small"
                                onClick={handleDrillDown}
                                disabled={getDrillLevel() >= DRILL_LEVEL.N2}
                                sx={drillButtonSx}
                            >
                                <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Box>
                <TraceabilityTreeTable
                    data={getFilteredTreeData()}
                    loading={loading}
                    onNodeSelect={handleNodeSelect}
                    selectedNode={selectedNode}
                    expandedNodes={expandedNodes}
                    onToggleNode={handleToggleNode}
                    maxDepth={getVisibleDepth() || data?.tree?.max_depth || 1}
                    extraPatients={extraPatients}
                    onLoadMorePatients={handleLoadMorePatients}
                    loadingMoreNodes={loadingMoreNodes}
                />
            </Box>

            {/* Bottom Section: Product Summary + Patients + Legend */}
            <Grid container spacing={2}>
                {/* Product Summary Table */}
                <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: "#1a365d", fontWeight: 600 }}>
                        {intl.get("Analytics.Traceability.ProductSummary").d("Resumo por Produto")}
                    </Typography>
                    <Box sx={{ position: "relative" }}>
                        <Box sx={drillButtonGroupSx}>
                            <Tooltip title={intl.get("Analytics.Tree.DrillUp").d("Recolher Nível")}>
                                <span>
                                    <IconButton
                                        size="small"
                                        onClick={() => productSummaryRef.current?.drillUp()}
                                        sx={drillButtonSx}
                                    >
                                        <KeyboardArrowUpIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                </span>
                            </Tooltip>
                            <Tooltip title={intl.get("Analytics.Tree.DrillDown").d("Expandir Nível")}>
                                <span>
                                    <IconButton
                                        size="small"
                                        onClick={() => productSummaryRef.current?.drillDown()}
                                        sx={drillButtonSx}
                                    >
                                        <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Box>
                        <ProductSummaryTable
                            ref={productSummaryRef}
                            data={getFilteredProductSummary()}
                            loading={loading}
                            onProductSelect={handleProductSelect}
                            selectedProductPk={selectedProduct?.pk}
                            onBatchSelect={handleBatchSelect}
                            selectedBatchNumber={selectedBatch?.number}
                        />
                    </Box>
                </Grid>

                {/* Patient Dispensations */}
                <Grid item xs={12} md={6}>
                    <Typography variant="subtitle2" sx={{ mb: 1, color: "#1a365d", fontWeight: 600 }}>
                        {intl.get("Analytics.Traceability.PatientDispensations").d("Dispensações por Paciente")}
                    </Typography>
                    <PatientDispensationTable
                        data={patientDisp}
                        loading={patientDispLoading}
                        onPatientClick={handlePatientDispClick}
                        selectedPatientCode={selectedPatientDisp?.code}
                        onScroll={handlePatientTableScroll}
                        loadingMore={patientDispHasMore && !patientDispLoading}
                    />
                </Grid>
            </Grid>
        </Box>
    );
});

DmsAnalyticsTraceability.displayName = "DmsAnalyticsTraceability";

DmsAnalyticsTraceability.propTypes = {
    url: PropTypes.string.isRequired,
    sx: PropTypes.object,
    onExpandStateChange: PropTypes.func,
};
